import React from 'react';
import { Activity, TrendingUp, DollarSign, Settings, LogOut, Briefcase } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

export const Layout = ({ children, activeTab = 'dashboard' }) => {
  const { user, logout } = useAuth();

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp, href: '/' },
    { id: 'cabinet', label: 'Cabinet', icon: Briefcase, href: '/cabinet' },
    { id: 'budget', label: 'Budget', icon: DollarSign, href: '/budget' },
    { id: 'settings', label: 'Settings', icon: Settings, href: '/settings' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="w-full max-w-[1800px] mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-display font-bold text-foreground">
                  Entity Trend Radar
                </h1>
                <p className="text-xs text-muted-foreground">Gambling Industry Analytics</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <nav className="hidden md:flex items-center gap-1">
                {tabs.map(tab => (
                  <a key={tab.id} href={tab.href} className={`nav-link ${activeTab === tab.id ? 'nav-link-active' : ''}`}>
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </a>
                ))}
              </nav>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user?.name}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="w-full max-w-[1800px] mx-auto px-4 lg:px-8 py-4">
        {children}
      </main>
      
      {/* Footer */}
      <footer className="border-t border-border mt-12">
        <div className="w-full max-w-[1800px] mx-auto px-4 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <p>© 2024 Entity Trend Radar. Built for gambling industry analytics.</p>
            <p>Powered by Google Suggest, YouTube, Trends & Ahrefs</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
